import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { AnimatedNumber } from '../AnimatedNumber';
import { duration } from '../arena-ui';
import { CORRECT_CALL_POINTS, type CoinView } from './model';
import { predictionState } from './predictionState';
import { previewBet } from './previewModel';
import { stakeTimerPaths } from './stakeTimer';
import { StakeContent } from './StakeContent';

// The prediction clock belongs to this frame; the result clock belongs to Run/Die.
export function PredictionStatus({
  coin,
  now,
  amount,
  onAmount,
  disabled = false,
}: {
  coin: CoinView;
  now: number;
  amount?: string;
  onAmount?: (amount: string) => void;
  disabled?: boolean;
}) {
  const state = predictionState(coin, now);
  const bet = previewBet(coin);
  const editable = !bet && state.kind === 'bought';
  const counting = state.kind === 'bought';
  const [draft, setDraft] = useState({
    id: coin.id,
    points: String(CORRECT_CALL_POINTS),
  });
  const points =
    amount ??
    (draft.id === coin.id ? draft.points : String(CORRECT_CALL_POINTS));
  const frame = useRef<HTMLLabelElement>(null);
  const inputId = useId();
  const clock = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState({ width: 360, height: 86, pill: 179 });
  useLayoutEffect(() => {
    const node = frame.current;
    if (!node) return;
    const measure = () => {
      const css = getComputedStyle(node);
      const next = {
        width: Number.parseFloat(css.width),
        height: Number.parseFloat(css.height),
        pill: clock.current
          ? Number.parseFloat(getComputedStyle(clock.current).width)
          : 179,
      };
      setSize((old) =>
        old.width === next.width &&
        old.height === next.height &&
        old.pill === next.pill
          ? old
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (clock.current) observer.observe(clock.current);
    return () => observer.disconnect();
  }, [counting]);
  const started = useRef({ timing: state.timing, elapsed: state.elapsed });
  if (started.current.timing !== state.timing)
    started.current = { timing: state.timing, elapsed: state.elapsed };
  const paths = stakeTimerPaths(size.width, size.height, size.pill);
  return (
    <label
      ref={frame}
      htmlFor={editable ? inputId : undefined}
      className={
        'stake-box' +
        (editable ? ' is-editable' : '') +
        (editable && !points ? ' is-empty' : '') +
        (bet?.result ? ' is-' + bet.result : '')
      }
      data-prediction-state={editable ? 'open' : state.kind}
      style={
        {
          '--stake-ms': state.total + 'ms',
          '--stake-delay': -started.current.elapsed + 'ms',
          '--remain': String(1 - state.elapsed / state.total),
        } as CSSProperties
      }
    >
      <svg
        className="stake-frame"
        viewBox={`0 0 ${size.width} ${size.height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <path d={paths.frame} />
        {bet?.result && <path className="stake-result-frame" d={paths.frame} />}
      </svg>
      {counting && (
        <svg
          className="stake-trace"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            key={state.timing + '-left'}
            className="stake-burn"
            pathLength="100"
            d={paths.left}
          />
          <path
            key={state.timing + '-right'}
            className="stake-burn"
            pathLength="100"
            d={paths.right}
          />
        </svg>
      )}
      <StakeContent
        state={[
          coin.id,
          editable ? 'open' : (bet?.choice ?? 'closed'),
          bet?.result ?? '',
        ].join(':')}
      >
        <span className="stake-details">
          <span className="stake-amount">
            {editable ? (
              <input
                id={inputId}
                inputMode={onAmount ? 'decimal' : 'numeric'}
                pattern={onAmount ? undefined : '[0-9]*'}
                value={points}
                placeholder="0.00"
                maxLength={onAmount ? 7 : 5}
                disabled={disabled}
                style={{ width: Math.max(2, points.length || 4) + 'ch' }}
                aria-label="Your bet"
                onChange={(event) => {
                  if (onAmount) {
                    onAmount(event.target.value);
                    return;
                  }
                  setDraft({
                    id: coin.id,
                    points: event.target.value.replace(/\D/g, '').slice(0, 5),
                  });
                }}
              />
            ) : (
              <AnimatedNumber
                value={
                  coin.lockedOdds?.amount ??
                  (coin.simulated && !coin.id.startsWith('game-')
                    ? CORRECT_CALL_POINTS
                    : '—')
                }
              />
            )}
            <span>PTS</span>
          </span>
          {bet && (
            <span
              className={
                'stake-choice is-' + (bet.choice === 'SURVIVES' ? 'up' : 'down')
              }
            >
              {bet.choice === 'SURVIVES' ? 'RUN!' : 'DIE!'}
            </span>
          )}
          {bet && state.multiplier !== null && (
            <span
              className="stake-odds"
              aria-label={
                coin.simulated && !coin.lockedOdds
                  ? 'Current crowd multiplier'
                  : 'Your fixed coefficient'
              }
            >
              <AnimatedNumber
                value={
                  state.multiplier.toFixed(
                    coin.simulated && !coin.lockedOdds ? 1 : 2,
                  ) + 'x'
                }
              />
            </span>
          )}
        </span>
        <span className="stake-hint">Your bet</span>
      </StakeContent>
      {counting && (
        <span ref={clock} className="stake-pill">
          <span>{coin.note ?? 'Prediction closes'}</span>
          <AnimatedNumber value={duration(state.remaining)} />
        </span>
      )}
      {bet?.result && (
        <span
          className="stake-pill stake-outcome"
          aria-label={bet.result === 'win' ? 'Your bet won' : 'Your bet lost'}
        >
          {bet.result === 'win' ? 'WIN' : 'LOSE'}
        </span>
      )}
    </label>
  );
}
